import type { FC } from 'react'

import { LoaderWrapper, StyledText, Table } from '@skytech/ui'
import { Td } from '@skytech/ui/Table/Td'
import { Th } from '@skytech/ui/Table/Th'
import { getQueryFilter, getSortedList } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import Highlight from '@/components/Highlight'
import { LoadingError } from '@/components/LoadingError'
import { useSortParams } from '@/hooks/searchParams'
import { useFormattedNumber } from '@/hooks/use-formatted-number'
import { useFormattedNumberWithCurrency } from '@/hooks/use-formatted-number-with-currency'
import { useLocalDate } from '@/hooks/use-local-date'
import { useRenderLimit } from '@/hooks/use-render-limit'
import { BreadCrumbs, Crumb } from '@/modules/admin/drilldown/BreadCrumbs'
import { DrillDownOptions } from '@/modules/admin/drilldown/DrillDownOptions'
import { getArraySumForSameCurrency } from '@/modules/admin/drilldown/helpers'
import { NoResultsWrapper } from '@/modules/admin/drilldown/NoResultsWrapper'
import queries from '@/modules/admin/queries'
import type {
  DrillDownParams,
  DrillDownType,
  ProductByCostObject as ProductByCostObjectType,
} from '@/modules/admin/resources'
import adminResources from '@/modules/admin/resources'
import { APP_ROUTES } from '@/util/app-routes'

import { useDrillDownContext } from '../context/index.context'
import { extractCurrencies, quantityText, valuesForExport } from '../utils'

export const ProductReportByCostObject: FC = () => {
  const { locale } = useAppContext()
  const { state } = useDrillDownContext()
  const { formatDate } = useLocalDate()

  const params: DrillDownParams = {
    type: state.type as DrillDownType,
    typeId: String(state.typeId),
    year: String(state.year),
    period: String(state.period),
    costObjectId: String(state.costObjectId),
  }

  const { data, isError } = useQuery(queries.adminDrillDownCostObjectProducts(params), () =>
    adminResources.getDrillDownProductByCostObjectsReport(params)
  )

  const stringMatcher = (item: ProductByCostObjectType) => [
    item.productName,
    formatDate({ date: item.fromPeriod }),
    formatDate({ date: item.toPeriod }),
    item.quantity,
    item.volume,
    item.amount,
    item.salaryDeductionAmount,
  ]

  const rowsFiltered =
    data?.rows?.filter(row => {
      const isCurrencyMatch = row.currency === state.currency
      const isAmountNotZero = row.amount !== 0
      const isSalaryDeductionAmountNotZero = row.salaryDeductionAmount !== 0

      const isQueryFilterMet = getQueryFilter(state.search, stringMatcher)(row)

      return (
        isCurrencyMatch && (isAmountNotZero || isSalaryDeductionAmountNotZero) && isQueryFilterMet
      )
    }) || []

  const [{ querySort, querySortBy }, { handleSort }] = useSortParams<keyof ProductByCostObjectType>(
    'desc',
    'amount'
  )

  const sortedList = getSortedList({
    list: rowsFiltered,
    querySort,
    querySortBy,
    locale,
  })

  const { visibleList, ListLoaderComponent } = useRenderLimit(sortedList || [], 30)

  if (isError) return <LoadingError />
  if (!data) return <LoaderWrapper isLoading />

  const currencies = extractCurrencies(data.rows)

  const totalAmount: number = getArraySumForSameCurrency(data?.rows, 'amount', state.currency)
  const totalSalaryDeductionAmount: number = getArraySumForSameCurrency(
    data?.rows,
    'salaryDeductionAmount',
    state.currency
  )

  return (
    <div>
      <BreadCrumbs>
        <Crumb
          name={data.entity.name}
          route={APP_ROUTES.REPORTS_DRILL_DOWN_COST_OBJECT}
          params={{
            type: state.type as DrillDownType,
            typeId: String(state.typeId),
            year: String(state.year),
            period: String(state.period),
          }}
        />
        <Crumb
          name={data.costObject.name}
          route={APP_ROUTES.REPORTS_DRILL_DOWN_COST_OBJECT_PRODUCT}
          params={params}
        />
      </BreadCrumbs>
      <DrillDownOptions
        data={sortedList.map(item => ({
          name: item.productName,
          'from period': item.fromPeriod,
          'to period': item.toPeriod,
          quantity: valuesForExport(item, 'quantity'),
          duration: valuesForExport(item, 'duration'),
          'mobile data': valuesForExport(item, 'data'),
          amount: item.amount,
          'salary deduction': item.salaryDeductionAmount,
          'part of cost': item.amount / totalAmount,
        }))}
        columnFormats={{
          'from period': 'date',
          'to period': 'date',
          quantity: 'number',
          duration: 'time_duration',
          'mobile data': 'mobile_data',
          amount: 'amount',
          'salary deduction': 'amount',
          'part of cost': 'percentage',
        }}
        title="Report"
        currencies={currencies}
        hasSearch
      />
      <NoResultsWrapper numOfResults={data.rows.length}>
        <Table>
          <thead>
            <tr>
              <Th id="productName" onClick={handleSort} className="px-4">
                <Translation id="name" />
              </Th>
              <Th id="fromPeriod" onClick={handleSort} className="px-4">
                <Translation id="from period" />
              </Th>
              <Th id="toPeriod" onClick={handleSort} className="px-4">
                <Translation id="to period" />
              </Th>
              <Th id="volume" onClick={handleSort} className="px-4 text-right">
                <Translation id="Volume" />
              </Th>
              <Th id="amount" onClick={handleSort} className="px-4 text-right">
                <Translation id="amount" />
              </Th>
              <Th id="salaryDeductionAmount" onClick={handleSort} className="px-4 text-right">
                <Translation id="salary deduction" />
              </Th>
              <th className="px-4 text-right">
                <Translation id="part of cost" />
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleList.map((product: ProductByCostObjectType) => (
              <Row key={product.productId} totalAmount={totalAmount} {...product} />
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="px-4">
                <StyledText>
                  <Translation id="total" />
                </StyledText>
              </td>
              <td className="px-4" />
              <td className="px-4" />
              <td className="px-4" />
              <td className="px-4 text-right">
                <StyledText>
                  <FormattedNumberWithCurrency
                    amount={totalAmount}
                    currency={state.currency}
                    locale={rowsFiltered?.[0]?.locale}
                  />
                </StyledText>
              </td>
              <td className="px-4 text-right">
                <StyledText>
                  <FormattedNumberWithCurrency
                    amount={totalSalaryDeductionAmount}
                    currency={state.currency}
                    locale={rowsFiltered?.[0]?.locale}
                  />
                </StyledText>
              </td>
            </tr>
          </tfoot>
        </Table>
        <ListLoaderComponent />
      </NoResultsWrapper>
    </div>
  )
}

const Row: FC<ProductByCostObjectType & { totalAmount: number }> = ({
  productName,
  amount,
  salaryDeductionAmount,
  totalAmount = 0,
  fromPeriod,
  toPeriod,
  quantity,
  peakVolumeDiff,
  priceType,
  currency,
  locale,
}) => {
  const { state } = useDrillDownContext()
  const { formatNumber: formatNumberWithCurrency } = useFormattedNumberWithCurrency()
  const { formatNumber } = useFormattedNumber()
  const { formatDate } = useLocalDate()

  return (
    <tr>
      <Td>
        <Highlight pattern={state.search} text={productName} />
      </Td>
      <Td>
        <Highlight pattern={state.search} text={formatDate({ date: fromPeriod })} />
      </Td>
      <Td>
        <Highlight pattern={state.search} text={formatDate({ date: toPeriod })} />
      </Td>
      <Td className="text-right">
        <Highlight
          pattern={state.search}
          text={quantityText({ quantity, priceType, peakVolumeDiff })}
        />
      </Td>
      <Td className="text-right">
        <Highlight
          pattern={state.search}
          text={formatNumberWithCurrency({
            amount,
            currency,
            locale,
          })}
        />
      </Td>
      <Td className="text-right">
        <Highlight
          pattern={state.search}
          text={formatNumberWithCurrency({
            amount: salaryDeductionAmount,
            currency,
            locale,
          })}
        />
      </Td>
      <Td className="text-right">
        {amount > 0 && (
          <>
            <Highlight
              pattern={state.search}
              text={formatNumber({
                amount: (amount / totalAmount) * 100,
                locale,
              })}
            />
            %
          </>
        )}
      </Td>
    </tr>
  )
}
